import { useEffect, useRef, useState } from 'react';
import { Lightbulb, Loader2, AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useAddLessonsLearnedEntry,
  useLessonsLearnedPage,
  LESSONS_CATEGORIES,
  type LessonsCategory,
} from '../../hooks/useLessonsLearned';
import { useCreateLessonsLearnedEntry } from '../../hooks/useLessonsLearnedEntries';
import { useMe } from '../../hooks/useMe';
import { useDomains } from '../../hooks/useDomains';
import { useActiveYear } from '../../contexts/ActiveYearContext';
import { Button, Modal } from '../common';

export default function LessonsLearnedButton() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex-shrink-0 p-2 rounded-lg text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        aria-label="Add lesson learned"
        title="Add lesson learned"
      >
        <Lightbulb className="w-5 h-5" aria-hidden="true" />
      </button>
      {isOpen && <LessonsLearnedModal onClose={() => setIsOpen(false)} />}
    </>
  );
}

function LessonsLearnedModal({ onClose }: { onClose: () => void }) {
  const { year } = useActiveYear();
  const { data: page, isLoading, error } = useLessonsLearnedPage(year);
  const addEntry = useAddLessonsLearnedEntry();
  const createEntryRow = useCreateLessonsLearnedEntry();
  const { me } = useMe();
  const { leafList: domainList } = useDomains();
  const authorName =
    [me?.given_name, me?.family_name].filter(Boolean).join(' ').trim() ||
    me?.email ||
    '';

  const [category, setCategory] = useState<LessonsCategory>('worked');
  const [domain, setDomain] = useState<string>('');
  const [text, setText] = useState('');
  const [details, setDetails] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isLoading && page) {
      textareaRef.current?.focus();
    }
  }, [isLoading, page]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!page) return;
    if (!text.trim()) return;
    try {
      // Write to BOTH stores so the modal stays compatible with the legacy
      // wiki page while the new /lessons-learned module is the canonical
      // surface going forward.
      await Promise.all([
        addEntry.mutateAsync({ page, category, text, authorName }),
        createEntryRow.mutateAsync({
          year: year ?? undefined,
          text,
          category,
          domain: domain || undefined,
          details: details.trim() || undefined,
          savedByName: authorName || undefined,
        }),
      ]);
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to save. Try again.'
      );
    }
  };

  const isSubmitting = addEntry.isPending || createEntryRow.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Add a lesson learned${year ? ` — ${year}` : ''}`}
      dismissOnBackdrop={false}
    >
      <form
        onSubmit={handleSubmit}
        className="contents"
      >
        <Modal.Body>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-yellow-500" aria-hidden="true" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Capture what worked, what didn't, and what to try next time.
            </span>
          </div>
          <div className="space-y-4">
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </span>
            <div className="flex flex-wrap gap-2">
              {LESSONS_CATEGORIES.map((c) => {
                const active = c.id === category;
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setCategory(c.id)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                    style={{
                      borderColor: c.color,
                      backgroundColor: active ? c.color : 'transparent',
                      color: active ? '#ffffff' : c.color,
                    }}
                    aria-pressed={active}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label
              htmlFor="lessons-learned-domain"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Domain <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span>
            </label>
            <select
              id="lessons-learned-domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">(no domain)</option>
              {domainList.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="lessons-learned-text"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              What happened?
            </label>
            <textarea
              id="lessons-learned-text"
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              placeholder="Quick note — specifics help future us remember the why."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {authorName && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Posting as <span className="font-medium">{authorName}</span>
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="lessons-learned-details"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Details <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span>
            </label>
            <textarea
              id="lessons-learned-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              placeholder="More context — root cause, links, who was involved."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 inline-flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              Couldn't load the Lessons Learned page.
            </p>
          )}
          {submitError && (
            <p className="text-sm text-red-600 dark:text-red-400 inline-flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              {submitError}
            </p>
          )}
          </div>
        </Modal.Body>
        <Modal.Footer align="between">
          {page ? (
            <Link
              to={`/wiki/${page.slug}`}
              className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
              onClick={onClose}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View full page
            </Link>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!page || !text.trim() || isSubmitting}
              leadingIcon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : undefined}
            >
              Save entry
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
